// useMissionBotRunner.js

import { useEffect, useRef } from 'react';

import { executeMissionBotTask } from 'services/missions/MissionBotService';
import { getPersonEmail } from 'utilities/PersonIdentity';
import {
  buildMissionBotSendMessageText,
  canProcessMissionBotTask,
  getMissionRunTaskLaunchRecipient,
} from 'node/missions/mission/utils/missionRunUtils';

/**
 * useMissionBotRunner
 *
 * Auto-executes ready Mission Bot tasks for the current node while the Missions surface is mounted.
 *
 * @param {object} options - Runner inputs.
 * @param {object|null} options.node - Current anchor node.
 * @param {object[]} options.missionEntries - Mission template/run entries.
 * @param {string} options.savingMissionKey - Template key currently persisting.
 * @param {object|null} options.typeDef - Current node type definition.
 */
export function useMissionBotRunner({
  baseKey = '',
  missionEntries = [],
  node = null,
  savingMissionKey = '',
  typeDef = null,
} = {}) {
  const executingBotTaskKeyRef = useRef('');

  useEffect(() => {
    if (!node?.key) return undefined;
    if (executingBotTaskKeyRef.current) return undefined;

    const resolvedBaseKey = String(
      baseKey
      || node?.baseKey
      || node?.workspaceKey
      || ''
    ).trim();
    if (!resolvedBaseKey) return undefined;

    const pendingEntry = missionEntries.find(({ missionRun, template }) => {
      if (!template?.key || !missionRun || savingMissionKey === template.key) return false;

      return missionRun.tasks.some((task) => {
        const botJob = String(task?.missionBot?.job || '').trim();
        if (botJob === 'runWorkspaceCommand') return false;
        return canProcessMissionBotTask(task);
      });
    }) || null;

    const pendingTask = pendingEntry?.missionRun?.tasks.find((task) => {
      const botJob = String(task?.missionBot?.job || '').trim();
      if (botJob === 'runWorkspaceCommand') return false;
      return canProcessMissionBotTask(task);
    }) || null;
    if (!pendingEntry?.template?.key || !pendingTask?.key) return undefined;

    const runBotTask = async () => {
      const taskKey = String(pendingTask?.key || '').trim();
      const botJob = String(pendingTask?.missionBot?.job || '').trim();
      const launchRecipient = getMissionRunTaskLaunchRecipient(pendingTask);
      const recipientUserKey = String(launchRecipient?.userKey || launchRecipient?.key || '').trim();
      const recipientEmail = String(
        getPersonEmail(launchRecipient)
        || launchRecipient?.email
        || launchRecipient?.data_profile?.email
        || ''
      ).trim().toLowerCase();
      const sendMessage = pendingTask?.missionBot?.config?.sendMessage || {};
      const messageText = buildMissionBotSendMessageText({
        node,
        task: pendingTask,
        template: pendingEntry.template,
        typeDef,
      });
      const emailSubject = String(sendMessage?.subject || '').trim() || `${pendingEntry.template?.title || 'Mission'} update`;

      executingBotTaskKeyRef.current = taskKey;

      try {
        await executeMissionBotTask({
          baseKey: resolvedBaseKey,
          botJob,
          nodeKey: String(node?.key || '').trim(),
          recipientEmails: recipientEmail ? [recipientEmail] : [],
          recipientUserKeys: recipientUserKey ? [recipientUserKey] : [],
          subject: emailSubject,
          taskKey,
          templateKey: String(pendingEntry.template?.key || '').trim(),
          text: messageText,
        });
      } catch (error) {
        console.error('Mission Bot execution failed', {
          baseKey: resolvedBaseKey,
          botJob,
          code: error?.code || null,
          details: error?.details || null,
          error: error?.message || String(error),
          nodeKey: String(node?.key || '').trim(),
          nodeBaseKey: String(node?.baseKey || node?.workspaceKey || '').trim(),
          taskKey,
          templateKey: String(pendingEntry.template?.key || '').trim(),
        });
      } finally {
        executingBotTaskKeyRef.current = '';
      }
    };

    runBotTask().catch((error) => {
      executingBotTaskKeyRef.current = '';
      console.error('Mission Bot runner failed', error?.message || String(error));
    });
  }, [baseKey, missionEntries, node, savingMissionKey, typeDef]);
}
